import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CardState, TabletopProps, TabletopItemType, TabletopState, DeckInsertionPosition, DeckState } from './types';
import { HandCardState } from '@/types/game';
import Card from '../card/Card';
import Deck from '../deck/Deck';
import DeckImporter from '../deck/DeckImporter';
import DeckSearchModal from '../deck/DeckSearchModal';
import QuickDeckButtons from '../deck/QuickDeckButtons';
import { calculateCenterPosition, calculateDeckHoverTarget, getScaledClientPosition, getScaledMousePosition } from './utils/positionUtils';
import { CARD_WIDTH, CARD_HEIGHT, ZOOMED_CARD_WIDTH, ZOOMED_CARD_HEIGHT } from './constants';
import { getPlayerColor } from '@/utils/playerColors';
import { compressDeckForUrl } from '@/utils/deckLoader';

// Hooks
import { useCanvasRendering } from './hooks/useCanvasRendering';
import { useZoom } from './hooks/useZoom';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useTabletopState } from './hooks/useTabletopState';
import { useTabletopItems } from './hooks/useTabletopItems';
import { useHandSelection } from './hooks/useHandSelection';
import { useUrlDeckLoader } from '@/hooks/useUrlDeckLoader';
import { useMultiplayerSession } from '@/multiplayer/useMultiplayerSession';
import { TabletopAction, TabletopEvent } from '@/state/tabletopActions';

// Components
import TabletopNotification from './components/TabletopNotification';
import ZoomedCardModal from './components/ZoomedCardModal';
import Dice from '../dice/Dice';
import Token from '../token/Token';
import Coin from '../coin/Coin';
import TokenEditModal from '../token/TokenEditModal';
import HandRail from '../hand/HandRail';
import OpponentHands from '../hand/OpponentHands';
import HandCardPreviewModal from '../hand/HandCardPreviewModal';
import { orderCardIdsForInteraction, sortCardIdsByLayer } from './utils/cardLayering';
import { CardStackDirection, getStackedCardPositions } from './utils/cardStacking';
import TabletopTopBar from './layout/TabletopTopBar';
import CommandDock, { CommandKey } from './layout/CommandDock';
import TableDrawer from './layout/TableDrawer';
import ContextActionBar from './layout/ContextActionBar';

type DeckSearchDragCard = {
  id: string;
  imageUrl?: string;
  backImageUrl?: string;
  name?: string;
};

interface DeckSearchDragState {
  deckId: string;
  cardIds: string[];
  startClient: { x: number; y: number };
  lastClient: { x: number; y: number };
  isDragging: boolean;
  cards: DeckSearchDragCard[];
}

export default function Tabletop({
  width = window.innerWidth,
  height = window.innerHeight,
  backgroundColor,
  initialState,
}: TabletopProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const actionForwardRef = useRef<(action: TabletopAction) => void>(() => {});
  const playerIdRef = useRef<string | null>(null);

  // State for showing deck importer modal
  const [showDeckImporter, setShowDeckImporter] = useState(false);
  const [isQuickDecksOpen, setIsQuickDecksOpen] = useState(false);
  const [deckSearchState, setDeckSearchState] = useState<{ deckId: string; selectedCardIds: string[] } | null>(null);
  const [zoomedDeckSearchCard, setZoomedDeckSearchCard] = useState<CardState | null>(null);
  const searchDragRef = useRef<DeckSearchDragState | null>(null);
  const [searchDragPreview, setSearchDragPreview] = useState<{ cards: DeckSearchDragCard[]; clientX: number; clientY: number } | null>(null);
  const [deckHoverPreview, setDeckHoverPreview] = useState<{ deckId: string; zone: DeckInsertionPosition } | null>(null);
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [hoveredTableCardId, setHoveredTableCardId] = useState<string | null>(null);
  const [opponentHandsBounds, setOpponentHandsBounds] = useState<DOMRect | null>(null);
  const [editingTokenId, setEditingTokenId] = useState<string | null>(null);
  const [sessionIdInput, setSessionIdInput] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    try {
      return new URLSearchParams(window.location.search).get('session') ?? '';
    } catch {
      return '';
    }
  });
  const [playerNameInput, setPlayerNameInput] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.localStorage.getItem('table-player-name') ?? '';
    } catch {
      return '';
    }
  });
  const [activePanel, setActivePanel] = useState<'decks' | 'chat' | 'history' | 'players' | 'help' | 'menu' | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [lastSeenChatCount, setLastSeenChatCount] = useState(0);
  
  // Theme detection
  const [bgColor, setBgColor] = useState(
    backgroundColor || getComputedStyle(document.documentElement).getPropertyValue('--tabletop-background').trim()
  );
  
  // Listen for theme changes
  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const updateBackgroundColor = () => {
      // Force recomputing the CSS variable value
      const newColor = getComputedStyle(document.documentElement).getPropertyValue('--tabletop-background').trim();
      setBgColor(newColor);
    };
    
    // Initial check
    updateBackgroundColor();
    
    // Add listener for theme changes
    darkModeMediaQuery.addEventListener('change', updateBackgroundColor);
    
    return () => {
      darkModeMediaQuery.removeEventListener('change', updateBackgroundColor);
    };
  }, []);

  // Initialize tabletop state
  const {
    tabletopState,
    setTabletopState,
    selectedItem,
    setSelectedItem,
    zoomedCard,
    setZoomedCard,
    notification,
    setNotification,
    handleDeckOperation,
    handleCardFlip,
    handleCardRotate,
    handleHandCardFlip,
    setHandCardSelection,
    handleDiceRoll,
    addTestDice,
    addToken,
    addCoin,
    updateToken,
    handleCoinFlip,
    handleDeckImport,
    addCardToDeck,
    moveDeckCardsToTable,
    moveDeckCardsToHand,
    moveDeckCardsToDeck,
    recordHistoryEntry,
    moveTableItem,
    moveCardGroup,
    rotateDeck,
    removeTableItem,
    showNotification,
    moveCardToHand,
    placeHandCardOnTable,
    handleCardAnimationComplete,
    applyRemoteState,
    setPlayerId
  } = useTabletopState({
    initialState,
    onAction: useCallback((action: TabletopAction) => actionForwardRef.current(action), [])
  });


  const handleRemoteUpdate = useCallback(
    (state: TabletopState, meta: { events?: TabletopEvent[]; initiatedBy?: string | null }) => {
      const skipEvents = Boolean(meta?.initiatedBy && meta.initiatedBy === playerIdRef.current);
      applyRemoteState(state, { events: meta?.events, skipEvents });
    },
    [applyRemoteState]
  );

  const {
    status: sessionStatus,
    sessionId: activeSessionId,
    playerId: currentPlayerId,
    players: sessionPlayers,
    lastError: sessionError,
    chatMessages,
    connect: connectSession,
    disconnect: disconnectSession,
    sendAction: sendSessionAction,
    sendChat: sendChatMessage
  } = useMultiplayerSession({
    onState: handleRemoteUpdate,
    onError: message => showNotification(message, 'error')
  });

  const viewerId = currentPlayerId ?? 'local';
  const viewerPlayer = useMemo(
    () => sessionPlayers.find(player => player.id === currentPlayerId) ?? null,
    [sessionPlayers, currentPlayerId]
  );
  const viewerSeatIndex = viewerPlayer?.seatIndex ?? 0;
  const viewerFacingRotation = viewerSeatIndex % 2 === 1 ? 180 : 0;
  const defaultInvertView = viewerSeatIndex % 2 === 1;

  const [invertViewOverride, setInvertViewOverride] = useState<boolean | null>(null);
  const invertView = useMemo(() => {
    if (invertViewOverride !== null) return invertViewOverride;
    return defaultInvertView;
  }, [defaultInvertView, invertViewOverride]);

  useEffect(() => {
    setSelectedCardIds(prev => {
      const filtered = prev.filter(cardId => Boolean(tabletopState.cards[cardId]));
      if (filtered.length !== prev.length) {
        return filtered;
      }
      for (let index = 0; index < filtered.length; index += 1) {
        if (filtered[index] !== prev[index]) {
          return filtered;
        }
      }
      return prev;
    });
  }, [tabletopState.cards]);

  useEffect(() => {
    if (selectedItem?.type === 'card') {
      setSelectedCardIds(prev => {
        if (prev.length === 1 && prev[0] === selectedItem.id) {
          return prev;
        }

        if (prev.length > 1 && prev.includes(selectedItem.id)) {
          return prev;
        }

        return [selectedItem.id];
      });
    } else if (!selectedItem && selectedCardIds.length > 0) {
      setSelectedCardIds([]);
    } else if (selectedItem && selectedItem.type !== 'card' && selectedCardIds.length > 0) {
      setSelectedCardIds([]);
    }
  }, [selectedItem, selectedCardIds]);

  useEffect(() => {
    if (!deckSearchState) {
      return;
    }

    const { deckId, selectedCardIds: selectedDeckCards } = deckSearchState;
    const activeDeck = tabletopState.decks[deckId];

    if (!activeDeck) {
      setZoomedDeckSearchCard(null);
      setDeckSearchState(null);
      return;
    }

    const availableIds = new Set(activeDeck.cards.map(card => card.id));
    if (zoomedDeckSearchCard && !availableIds.has(zoomedDeckSearchCard.id)) {
      setZoomedDeckSearchCard(null);
    }

    if (selectedDeckCards.length === 0) {
      return;
    }

    const filtered = selectedDeckCards.filter(id => availableIds.has(id));

    if (filtered.length !== selectedDeckCards.length) {
      setDeckSearchState(prev => {
        if (!prev || prev.deckId !== deckId) {
          return prev;
        }
        return {
          ...prev,
          selectedCardIds: filtered
        };
      });
    }
  }, [deckSearchState, setDeckSearchState, tabletopState.decks, zoomedDeckSearchCard]);

  // Get the player's color for selections
  const playerSelectionColor = useMemo(() => {
    return currentPlayerId ? getPlayerColor(currentPlayerId).hex : undefined;
  }, [currentPlayerId]);

  const playerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    sessionPlayers.forEach(player => {
      map[player.id] = player.id === currentPlayerId ? 'You' : player.name;
    });
    if (!currentPlayerId) {
      map['local'] = 'You';
    }
    return map;
  }, [sessionPlayers, currentPlayerId]);

  useEffect(() => {
    playerIdRef.current = currentPlayerId;
    setPlayerId(currentPlayerId ?? null);
  }, [currentPlayerId, setPlayerId]);

  useEffect(() => {
    actionForwardRef.current = action => {
      if (sessionStatus === 'connected' || sessionStatus === 'connecting' || sessionStatus === 'reconnecting') {
        sendSessionAction(action);
      }
    };
    return () => {
      actionForwardRef.current = () => {};
    };
  }, [sendSessionAction, sessionStatus]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('table-player-name', playerNameInput);
    } catch (error) {
      console.debug('Unable to persist player name', error);
    }
  }, [playerNameInput]);

  // Auto-connect from URL parameter on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlSession = new URLSearchParams(window.location.search).get('session');
    if (!urlSession?.trim()) return;

    // Only auto-connect if not already connected/connecting
    if (sessionStatus !== 'idle') return;

    // Read player name from localStorage at connection time (not from state to avoid timing issues)
    let playerName = 'Player';
    try {
      playerName = window.localStorage.getItem('table-player-name')?.trim() || 'Player';
    } catch {
      // Fallback to default if localStorage is unavailable
    }

    connectSession(urlSession.trim(), playerName);
  }, [sessionStatus, connectSession]); // Re-run when session status changes

  const handleConnect = useCallback(() => {
    const trimmedId = sessionIdInput.trim();
    if (!trimmedId) {
      showNotification('Enter a session ID to connect', 'error');
      return;
    }
    const name = playerNameInput.trim() || 'Player';
    connectSession(trimmedId, name);
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set('session', trimmedId);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
  }, [sessionIdInput, playerNameInput, connectSession, showNotification]);

  const handleDisconnect = useCallback(() => {
    disconnectSession();

    // Keep session ID and player name so user can easily reconnect
    // Just remove session from URL
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.delete('session');
      const query = params.toString();
      window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
    }
  }, [disconnectSession]);

  // Initialize zoom state with our improved hook
  const { 
    zoomState, 
    isTableDragging,
    handleTableDragStart,
    handleTableDrag,
    handleTableDragEnd,
    handleZoom,
    resetZoom
  } = useZoom({
    invertView
  });
  const { position: zoomPosition, scale: zoomScale } = zoomState;

  // Initialize URL deck loader
  useUrlDeckLoader({
    onDeckLoad: handleDeckImport,
    onError: message => showNotification(message, 'error'),
    width,
    height,
    zoomState
  });

  // Initialize drag and drop
  const [handBounds, setHandBounds] = useState<DOMRect | null>(null);
  const [isHandHighlighted, setIsHandHighlighted] = useState(false);

  const {
    selectedHandCardId,
    previewHandCard,
    selectHandCard,
    clearHandSelection,
    setHoveredHandCardId,
    setPreviewHandCardId
  } = useHandSelection({
    hand: tabletopState.hand,
    handOrder: tabletopState.handOrder,
    viewerId,
    onFlipHandCard: handleHandCardFlip,
    setHandCardSelection
  });

  const handleHandBoundsChange = useCallback((rect: DOMRect | null) => {
    if (!rect) {
      setHandBounds(current => (current === null ? current : null));
      setIsHandHighlighted(false);
      return;
    }

    setHandBounds(prev => {
      if (
        prev &&
        prev.left === rect.left &&
        prev.top === rect.top &&
        prev.width === rect.width &&
        prev.height === rect.height
      ) {
        return prev;
      }
      return rect;
    });
  }, []);

  const {
    dragState,
    setDragState,
    handleItemDragStart,
    handleMouseMove: handleDragMove,
    handleMouseUp: handleDragEnd
  } = useDragAndDrop({
    tabletopState,
    setTabletopState,
    setSelectedItem,
    addCardToDeck,
    moveTableItem,
    moveCardGroup,
    zoomState,
    handBounds,
    moveCardToHand,
    selectedCardIds,
    setSelectedCardIds,
    onHandHoverChange: setIsHandHighlighted,
    invertView
  });

  const handleDeckSearchSelectionChange = useCallback((cardIds: string[]) => {
    setDeckSearchState(prev => {
      if (!prev) {
        return prev;
      }

      if (prev.selectedCardIds.length === cardIds.length && prev.selectedCardIds.every((id, index) => id === cardIds[index])) {
        return prev;
      }

      return {
        ...prev,
        selectedCardIds: cardIds
      };
    });
  }, [setDeckSearchState]);

  const handleDeckSearchMouseMove = useCallback((event: MouseEvent) => {
    const state = searchDragRef.current;
    if (!state) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY;
    state.lastClient = { x: clientX, y: clientY };

    if (!state.isDragging) {
      const dx = clientX - state.startClient.x;
      const dy = clientY - state.startClient.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 6) {
        return;
      }

      state.isDragging = true;
      setZoomedDeckSearchCard(null);
      setDeckSearchState(null);
      setSearchDragPreview({
        cards: state.cards,
        clientX,
        clientY
      });
    } else {
      setSearchDragPreview(prev => {
        if (!prev) {
          return {
            cards: state.cards,
            clientX,
            clientY
          };
        }

        if (prev.clientX === clientX && prev.clientY === clientY) {
          return prev;
        }

        return {
          ...prev,
          clientX,
          clientY
        };
      });
    }

    if (state.isDragging && handBounds) {
      const isOverHand =
        clientX >= handBounds.left &&
        clientX <= handBounds.right &&
        clientY >= handBounds.top &&
        clientY <= handBounds.bottom;
      setIsHandHighlighted(isOverHand);
    }
    
    if (!state.isDragging) {
      setDeckHoverPreview(prev => (prev ? null : prev));
      return;
    }

    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) {
      setDeckHoverPreview(prev => (prev ? null : prev));
      return;
    }

    const worldPosition = getScaledClientPosition(
      clientX,
      clientY,
      containerRect,
      zoomState.position,
      zoomState.scale,
      invertView
    );

    const dropPosition = {
      x: worldPosition.x - CARD_WIDTH / 2,
      y: worldPosition.y - CARD_HEIGHT / 2
    };

    const hoverTarget = calculateDeckHoverTarget(dropPosition, tabletopState.decks, CARD_WIDTH, CARD_HEIGHT);
    setDeckHoverPreview(prev => {
      if (!hoverTarget.deckId || !hoverTarget.zone) {
        return prev ? null : prev;
      }
      if (prev?.deckId === hoverTarget.deckId && prev.zone === hoverTarget.zone) {
        return prev;
      }
      return { deckId: hoverTarget.deckId, zone: hoverTarget.zone };
    });
  }, [handBounds, invertView, setDeckSearchState, setIsHandHighlighted, setSearchDragPreview, tabletopState.decks, zoomState.position, zoomState.scale]);

  const handleDeckSearchMouseUp = useCallback((event: MouseEvent) => {
    window.removeEventListener('mousemove', handleDeckSearchMouseMove);
    window.removeEventListener('mouseup', handleDeckSearchMouseUp);

    const state = searchDragRef.current;
    if (!state) {
      setSearchDragPreview(null);
      setIsHandHighlighted(false);
      setDeckHoverPreview(null);
      return;
    }

    const wasDragging = state.isDragging;
    const deckId = state.deckId;
    const cardIds = state.cardIds;

    searchDragRef.current = null;
    setSearchDragPreview(null);
    setIsHandHighlighted(false);
    setDeckHoverPreview(null);

    if (!wasDragging || cardIds.length === 0) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY;

    const isOverHand =
      handBounds &&
      clientX >= handBounds.left &&
      clientX <= handBounds.right &&
      clientY >= handBounds.top &&
      clientY <= handBounds.bottom;

    if (isOverHand) {
      moveDeckCardsToHand(deckId, cardIds);
      setZoomedDeckSearchCard(null);
      setDeckSearchState(null);
      return;
    }

    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) {
      const deck = tabletopState.decks[deckId];
      if (deck) {
        moveDeckCardsToTable(
          deckId,
          cardIds,
          { x: deck.position.x, y: deck.position.y },
          { faceUp: true, rotation: viewerFacingRotation }
        );
      }
      setZoomedDeckSearchCard(null);
      setDeckSearchState(null);
      return;
    }

    const worldPosition = getScaledClientPosition(
      clientX,
      clientY,
      containerRect,
      zoomState.position,
      zoomState.scale,
      invertView
    );

    const dropPosition = {
      x: worldPosition.x - CARD_WIDTH / 2,
      y: worldPosition.y - CARD_HEIGHT / 2
    };

    const hoverTarget = calculateDeckHoverTarget(dropPosition, tabletopState.decks, CARD_WIDTH, CARD_HEIGHT);

    if (hoverTarget.deckId) {
      moveDeckCardsToDeck(deckId, hoverTarget.deckId, cardIds, { position: hoverTarget.zone ?? 'top' });
      setZoomedDeckSearchCard(null);
      setDeckSearchState(null);
      return;
    }

    moveDeckCardsToTable(
      deckId,
      cardIds,
      dropPosition,
      { faceUp: true, rotation: viewerFacingRotation }
    );
    setZoomedDeckSearchCard(null);
    setDeckSearchState(null);
  }, [
    containerRef,
    handBounds,
    invertView,
    moveDeckCardsToHand,
    moveDeckCardsToDeck,
    moveDeckCardsToTable,
    setDeckSearchState,
    setIsHandHighlighted,
    setSearchDragPreview,
    tabletopState.decks,
    viewerFacingRotation,
    zoomState.position,
    zoomState.scale,
    handleDeckSearchMouseMove
  ]);

  const handleDeckSearchDragStart = useCallback((cardIds: string[], event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    if (searchDragRef.current) {
      return;
    }

    const deckId = deckSearchState?.deckId;
    if (!deckId) {
      return;
    }

    const deck = tabletopState.decks[deckId];
    if (!deck) {
      return;
    }

    const uniqueIds = cardIds.filter((id, index, arr) => arr.indexOf(id) === index);
    const cards = uniqueIds
      .map(id => deck.cards.find(card => card.id === id))
      .filter((card): card is DeckState['cards'][number] => Boolean(card))
      .map(card => ({
        id: card.id,
        imageUrl: card.imageUrl,
        backImageUrl: card.backImageUrl,
        name: card.name
      }));

    if (cards.length === 0) {
      return;
    }

    searchDragRef.current = {
      deckId,
      cardIds: cards.map(card => card.id),
      startClient: { x: event.clientX, y: event.clientY },
      lastClient: { x: event.clientX, y: event.clientY },
      isDragging: false,
      cards
    };

    window.addEventListener('mousemove', handleDeckSearchMouseMove);
    window.addEventListener('mouseup', handleDeckSearchMouseUp);
  }, [deckSearchState?.deckId, handleDeckSearchMouseMove, handleDeckSearchMouseUp, tabletopState.decks]);

  const suppressNextTableSelectionRef = useRef(false);
  const suppressNextTableSelectionTimeoutRef = useRef<number | null>(null);

  const handleTableItemSelect = useCallback((id: string, type: TabletopItemType, event: React.MouseEvent) => {
    if (suppressNextTableSelectionRef.current && event.type === 'click') {
      event.stopPropagation();
      suppressNextTableSelectionRef.current = false;
      if (suppressNextTableSelectionTimeoutRef.current !== null) {
        window.clearTimeout(suppressNextTableSelectionTimeoutRef.current);
        suppressNextTableSelectionTimeoutRef.current = null;
      }
      return;
    }

    clearHandSelection();

    if (type === 'card') {
      const targetCard = tabletopState.cards[id];
      const cardAlreadySelected = selectedCardIds.includes(id);

      if (event.type === 'click' && event.shiftKey && targetCard) {
        const allCards = Object.values(tabletopState.cards);
        const rectById = new Map(
          allCards.map(card => [
            card.id,
            {
              left: card.position.x,
              top: card.position.y,
              right: card.position.x + CARD_WIDTH,
              bottom: card.position.y + CARD_HEIGHT
            }
          ])
        );

        const rectanglesIntersect = (aId: string, bId: string) => {
          const a = rectById.get(aId);
          const b = rectById.get(bId);
          if (!a || !b) return false;

          return (
            a.left <= b.right &&
            a.right >= b.left &&
            a.top <= b.bottom &&
            a.bottom >= b.top
          );
        };

        const cluster = new Set<string>();
        const queue: string[] = [id];

        while (queue.length > 0) {
          const currentId = queue.shift()!;
          if (cluster.has(currentId)) continue;
          cluster.add(currentId);

          for (const card of allCards) {
            if (!cluster.has(card.id) && rectanglesIntersect(currentId, card.id)) {
              queue.push(card.id);
            }
          }
        }

        const groupedIds = orderCardIdsForInteraction(Array.from(cluster), tabletopState.cards, id);
        const selectedSet = new Set(selectedCardIds);
        groupedIds.forEach(cardId => selectedSet.add(cardId));
        const additiveSelection = orderCardIdsForInteraction(Array.from(selectedSet), tabletopState.cards, id);

        setSelectedCardIds(additiveSelection.length > 0 ? additiveSelection : [id]);
      } else if (event.type === 'mousedown' && event.shiftKey) {
        // The follow-up click handles additive cluster selection. Preserve the
        // current selection during mousedown so Shift-click can extend it.
      } else if (!cardAlreadySelected) {
        setSelectedCardIds([id]);
      }
    } else if (selectedCardIds.length > 0) {
      setSelectedCardIds([]);
    }

    setSelectedItem({ id, type });
  }, [clearHandSelection, tabletopState.cards, selectedCardIds, setSelectedItem]);

  const handDragInfoRef = useRef<{ cardId: string; startClient: { x: number; y: number }; hasPlaced: boolean } | null>(null);

  const handleHandCardMouseMove = useCallback((event: MouseEvent) => {
    event.preventDefault();
    const info = handDragInfoRef.current;
    if (!info) return;
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    if (!info.hasPlaced) {
      const dragDistance = Math.hypot(event.clientX - info.startClient.x, event.clientY - info.startClient.y);
      if (dragDistance < 8) {
        return;
      }

      const syntheticEvent = {
        clientX: event.clientX,
        clientY: event.clientY,
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => {},
        shiftKey: event.shiftKey
      } as unknown as React.MouseEvent<HTMLDivElement>;

      const tablePoint = getScaledMousePosition(syntheticEvent, containerRect, zoomPosition, zoomScale, invertView);
      const initialPosition = {
        x: tablePoint.x - CARD_WIDTH / 2,
        y: tablePoint.y - CARD_HEIGHT / 2
      };

      const placedCard = placeHandCardOnTable(info.cardId, initialPosition, {
        faceUp: true,
        rotation: viewerFacingRotation,
        suppressLog: true
      });
      if (!placedCard) {
        handDragInfoRef.current = null;
        return;
      }

      setSelectedItem({ id: info.cardId, type: 'card' });
      setDragState(prev => ({
        ...prev,
        isDragging: true,
        draggingItem: { id: info.cardId, type: 'card' },
        cardOffset: { x: CARD_WIDTH / 2, y: CARD_HEIGHT / 2 },
        origin: 'hand',
        hoveringDeckId: null,
        hoveringDeckZone: null,
        hoveringHand: false,
        groupOffsets: undefined
      }));
      setIsHandHighlighted(false);
      clearHandSelection();

      info.hasPlaced = true;
      return;
    }

    const syntheticEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
      preventDefault: () => event.preventDefault(),
      stopPropagation: () => {},
      shiftKey: event.shiftKey
    } as unknown as React.MouseEvent<HTMLDivElement>;
    handleDragMove(syntheticEvent, containerRect);
  }, [placeHandCardOnTable, setSelectedItem, setDragState, handleDragMove, zoomPosition, zoomScale, invertView, viewerFacingRotation, clearHandSelection]);

  const handleHandCardMouseUp = useCallback((event: MouseEvent) => {
    event.preventDefault();
    const info = handDragInfoRef.current;
    window.removeEventListener('mousemove', handleHandCardMouseMove);
    window.removeEventListener('mouseup', handleHandCardMouseUp);

    if (!info) return;

    const container = containerRef.current;
    if (container && info.hasPlaced) {
      suppressNextTableSelectionRef.current = true;
      if (suppressNextTableSelectionTimeoutRef.current !== null) {
        window.clearTimeout(suppressNextTableSelectionTimeoutRef.current);
      }
      suppressNextTableSelectionTimeoutRef.current = window.setTimeout(() => {
        suppressNextTableSelectionRef.current = false;
        suppressNextTableSelectionTimeoutRef.current = null;
      }, 0);

      const containerRect = container.getBoundingClientRect();
      const syntheticEvent = {
        clientX: event.clientX,
        clientY: event.clientY,
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => {},
        shiftKey: event.shiftKey
      } as unknown as React.MouseEvent<HTMLDivElement>;
      handleDragEnd(syntheticEvent, containerRect);
    }

    handDragInfoRef.current = null;
  }, [handleHandCardMouseMove, handleDragEnd]);

  const handleHandCardMouseDown = useCallback((cardId: string, event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    handDragInfoRef.current = {
      cardId,
      startClient: { x: event.clientX, y: event.clientY },
      hasPlaced: false
    };
    window.addEventListener('mousemove', handleHandCardMouseMove);
    window.addEventListener('mouseup', handleHandCardMouseUp);
  }, [handleHandCardMouseMove, handleHandCardMouseUp]);

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleHandCardMouseMove);
      window.removeEventListener('mouseup', handleHandCardMouseUp);
      if (suppressNextTableSelectionTimeoutRef.current !== null) {
        window.clearTimeout(suppressNextTableSelectionTimeoutRef.current);
      }
    };
  }, [handleHandCardMouseMove, handleHandCardMouseUp]);

  // Initialize canvas rendering
  const { renderCanvas } = useCanvasRendering({
    canvasRef,
    width,
    height,
    bgColor,
    zoomState
  });

  // Create a clearSelection function
  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    clearHandSelection();
    setSelectedCardIds([]);
  }, [setSelectedItem, clearHandSelection]);

  // Handle token edit
  const handleEditToken = useCallback((id: string) => {
    setEditingTokenId(id);
  }, []);

  // Handle token save
  const handleSaveToken = useCallback((id: string, text: string, color: string) => {
    updateToken(id, text, color);
  }, [updateToken]);

  // Use the tabletop items hook instead of defining handlers inline
  const { itemHandlers } = useTabletopItems({
    setZoomedCard,
    handleCardFlip,
    handleDeckOperation,
    onRollDice: handleDiceRoll,
    handleCardRotate,
    moveCardToHand,
    removeTableItem,
    rotateDeck,
    onEditToken: handleEditToken,
    onFlipCoin: handleCoinFlip
  });

  // Initialize keyboard shortcuts
  useKeyboardShortcuts({
    selectedItem,
    selectedCardIds,
    tabletopState,
    setTabletopState,
    clearSelection,
    setZoomedCard,
    zoomedCard,
    hoveredTableCardId,
    itemHandlers,
    moveCardGroup
  });

  useEffect(() => {
    if (selectedItem) {
      clearHandSelection();
    }
  }, [selectedItem, clearHandSelection]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      renderCanvas();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [renderCanvas]);

  // Get center position for new items
  const centerPosition = useMemo(
    () =>
      calculateCenterPosition(
        width,
        height,
        zoomState.position,
        zoomState.scale
      ),
    [width, height, zoomState.position, zoomState.scale]
  );

  const activeSearchDeck = deckSearchState ? tabletopState.decks[deckSearchState.deckId] : undefined;
  const actionBarTopOffset = opponentHandsBounds
    ? Math.max(84, Math.ceil(opponentHandsBounds.bottom + 12))
    : undefined;

  const { myHandCards, opponentHands } = useMemo(() => {
    const grouped = new Map<string, HandCardState[]>();
    const order = tabletopState.handOrder ?? [];

    order.forEach(cardId => {
      const card = tabletopState.hand[cardId];
      if (!card) return;
      const ownerIdRaw = card.owner ?? viewerId;
      const normalizedOwner = ownerIdRaw === 'local' ? viewerId : ownerIdRaw;
      const existing = grouped.get(normalizedOwner);
      if (existing) {
        existing.push(card);
      } else {
        grouped.set(normalizedOwner, [card]);
      }
    });

    const myCards = grouped.get(viewerId) ?? [];
    grouped.delete(viewerId);

    const opponents: Array<{ ownerId: string; cards: HandCardState[] }> = [];

    const sortedPlayers = [...sessionPlayers].sort((a, b) => a.name.localeCompare(b.name));
    sortedPlayers.forEach(player => {
      if (player.id === viewerId) return;
      const cards = grouped.get(player.id) ?? [];
      grouped.delete(player.id);
      opponents.push({ ownerId: player.id, cards });
    });

    grouped.forEach((cards, ownerId) => {
      opponents.push({ ownerId, cards });
    });

    return { myHandCards: myCards, opponentHands: opponents };
  }, [tabletopState.hand, tabletopState.handOrder, viewerId, sessionPlayers]);


  // Handle test dice addition
  const handleAddTestDice = () => {
    addTestDice(centerPosition);
  };

  // Handle token addition
  const handleAddToken = () => {
    addToken(centerPosition, '0', '#ff6b6b');
  };

  const handleAddCoin = () => {
    addCoin(centerPosition);
  };

  const handleAddDiscardPile = useCallback(() => {
    const existingNames = new Set(Object.values(tabletopState.decks).map(deck => deck.name));
    let baseName = 'Discard Pile';
    let suffix = 2;

    while (existingNames.has(baseName)) {
      baseName = `Discard Pile ${suffix}`;
      suffix += 1;
    }

    const existingPileCount = Object.values(tabletopState.decks).filter(
      deck => deck.faceUp && deck.name.startsWith('Discard Pile')
    ).length;

    const discardPosition = {
      x: centerPosition.x + existingPileCount * (CARD_WIDTH + 24),
      y: centerPosition.y
    };

    const discardPile: DeckState = {
      id: uuidv4(),
      position: discardPosition,
      rotation: 0,
      name: baseName,
      cards: [],
      faceUp: true,
      collapsed: false
    };

    handleDeckImport(discardPile);
  }, [centerPosition, handleDeckImport, tabletopState.decks]);

  const handleDrawToHand = useCallback(
    (count: number) => {
      if (selectedItem?.type === 'deck') {
        handleDeckOperation(selectedItem.id, { type: 'drawToHand', count });
      } else {
        showNotification('Select a deck to draw cards into your hand', 'info');
      }
    },
    [selectedItem, handleDeckOperation, showNotification]
  );

  const handleDrawSingleToHand = useCallback(() => handleDrawToHand(1), [handleDrawToHand]);

  const handleCommand = useCallback((command: CommandKey) => {
    switch (command) {
      case 'decks':
      case 'chat':
      case 'history':
      case 'players':
      case 'help':
        setActivePanel(prev => (prev === command ? null : command));
        return;
      case 'discard':
        handleAddDiscardPile();
        return;
      case 'dice':
        handleAddTestDice();
        return;
      case 'token':
        handleAddToken();
        return;
      case 'coin':
        handleAddCoin();
        return;
      default:
        return;
    }
  }, [handleAddDiscardPile, handleAddCoin]);

  const handleDeckSearchOpen = useCallback((deckId: string) => {
    const deck = tabletopState.decks[deckId];
    if (!deck) return;
    recordHistoryEntry(`Began searching "${deck.name}".`);
    setZoomedDeckSearchCard(null);
    setDeckSearchState({ deckId, selectedCardIds: [] });
    setActivePanel(null);
  }, [recordHistoryEntry, tabletopState.decks]);

  const handleDeckShare = useCallback(async (deckId: string) => {
    const selectedDeck = tabletopState.decks[deckId];
    if (!selectedDeck) {
      showNotification('Select a deck to share', 'error');
      return;
    }

    try {
      const compressedDeck = compressDeckForUrl(selectedDeck);
      const shareUrlTarget = new URL(window.location.href);
      shareUrlTarget.searchParams.delete('session');
      shareUrlTarget.searchParams.set('deck', compressedDeck);
      const shareUrl = shareUrlTarget.toString();

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      showNotification('Deck link copied', 'success');
    } catch (error) {
      console.error('Failed to share deck:', error);
      showNotification('Failed to copy deck link', 'error');
    }
  }, [showNotification, tabletopState.decks]);

  const handleDeleteSelectedItem = useCallback((type: 'card' | 'deck' | 'dice' | 'token' | 'coin', id: string) => {
    removeTableItem(type, id);
    setSelectedItem(null);
    if (type === 'card') {
      setSelectedCardIds(prev => prev.filter(cardId => cardId !== id));
    }
  }, [removeTableItem, setSelectedItem]);

  const handleFlipCards = useCallback((cardIds: string[]) => {
    cardIds.forEach(cardId => {
      if (tabletopState.cards[cardId]) {
        handleCardFlip(cardId);
      }
    });
  }, [handleCardFlip, tabletopState.cards]);

  const handleRotateCards = useCallback((cardIds: string[]) => {
    cardIds.forEach(cardId => {
      const card = tabletopState.cards[cardId];
      if (card) {
        handleCardRotate(cardId, (card.rotation + 90) % 360);
      }
    });
  }, [handleCardRotate, tabletopState.cards]);

  const handleMoveCardsToHand = useCallback((cardIds: string[]) => {
    cardIds.forEach(cardId => {
      if (tabletopState.cards[cardId]) {
        moveCardToHand(cardId);
      }
    });
    setSelectedCardIds([]);
    setSelectedItem(null);
  }, [moveCardToHand, setSelectedItem, tabletopState.cards]);

  const handleStackSelectedCards = useCallback((direction: CardStackDirection) => {
    if (selectedCardIds.length < 2) {
      return;
    }

    const layout = getStackedCardPositions(selectedCardIds, tabletopState.cards, direction);
    if (layout.cardIds.length > 1) {
      moveCardGroup(layout.cardIds, layout.positions);
      setSelectedCardIds(layout.cardIds);
      setSelectedItem({ id: layout.cardIds[layout.cardIds.length - 1], type: 'card' });
    }
  }, [moveCardGroup, selectedCardIds, setSelectedItem, tabletopState.cards]);

  const handleSendChatFromDrawer = useCallback(() => {
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    sendChatMessage(trimmed);
    setChatInput('');
  }, [chatInput, sendChatMessage]);

  const formatChatTime = useCallback((timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  useEffect(() => {
    if (activePanel === 'chat') {
      setLastSeenChatCount(chatMessages.length);
    }
  }, [activePanel, chatMessages.length]);

  const handleSelectHandCard = useCallback((cardId: string) => {
    setSelectedItem(null);
    selectHandCard(cardId);
  }, [setSelectedItem, selectHandCard]);

  const handleCloseHandPreview = useCallback(() => {
    setPreviewHandCardId(null);
  }, [setPreviewHandCardId]);

  const handleCloseDeckSearch = useCallback(() => {
    setZoomedDeckSearchCard(null);
    setDeckSearchState(null);
  }, []);

  const draggingDeckIdRef = useRef<string | null>(null);
  const selectedDeckIdRef = useRef<string | null>(null);
  const pendingDrawTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const pendingDrawDeckIdRef = useRef<string | null>(null);

  const clearPendingDraw = useCallback(() => {
    if (pendingDrawTimeoutRef.current !== null) {
      window.clearTimeout(pendingDrawTimeoutRef.current);
      pendingDrawTimeoutRef.current = null;
    }
    pendingDrawDeckIdRef.current = null;
  }, []);

  const drawSelectedDeckToHand = useCallback(
    (deckId: string, count: number) => {
      handleDeckOperation(deckId, { type: 'drawToHand', count });
    },
    [handleDeckOperation]
  );

  useEffect(() => {
    draggingDeckIdRef.current =
      dragState.draggingItem?.type === 'deck' ? dragState.draggingItem.id : null;
  }, [dragState.draggingItem]);

  useEffect(() => {
    selectedDeckIdRef.current =
      selectedItem?.type === 'deck' ? selectedItem.id : null;
  }, [selectedItem]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (deckSearchState) {
        return;
      }

      if (e.key === 'Home') {
        e.preventDefault();
        resetZoom();
        return;
      }

      if (/^[2-9]$/.test(e.key) && pendingDrawDeckIdRef.current) {
        e.preventDefault();
        const pendingDeckId = pendingDrawDeckIdRef.current;
        const count = Number(e.key);
        clearPendingDraw();
        drawSelectedDeckToHand(pendingDeckId, count);
        return;
      }

      if (pendingDrawDeckIdRef.current && e.key !== 'Shift' && e.key !== 'Alt' && e.key !== 'Control' && e.key !== 'Meta') {
        clearPendingDraw();
      }

      if (e.key.toLowerCase() === 'g') {
        const selectedDeckId = selectedDeckIdRef.current;
        if (!selectedDeckId) {
          return;
        }

        const deck = tabletopState.decks[selectedDeckId];
        if (!deck) {
          return;
        }

        e.preventDefault();
        const confirmed = window.confirm(`Search "${deck.name}"?`);
        if (confirmed) {
          recordHistoryEntry(`Began searching "${deck.name}".`);
          setZoomedDeckSearchCard(null);
          setDeckSearchState({ deckId: selectedDeckId, selectedCardIds: [] });
        }
        return;
      }

      if (e.key.toLowerCase() === 'd') {
        const draggingDeckId = draggingDeckIdRef.current;
        const selectedDeckId = selectedDeckIdRef.current;

        if (draggingDeckId) {
          e.preventDefault();
          const deckPosition = tabletopState.decks[draggingDeckId]?.position;
          handleDeckOperation(draggingDeckId, { type: 'drawBelow', count: 1, position: deckPosition });
          return;
        }

        if (selectedDeckId) {
          e.preventDefault();
          clearPendingDraw();
          pendingDrawDeckIdRef.current = selectedDeckId;
          pendingDrawTimeoutRef.current = window.setTimeout(() => {
            const pendingDeckId = pendingDrawDeckIdRef.current;
            clearPendingDraw();
            if (pendingDeckId) {
              drawSelectedDeckToHand(pendingDeckId, 1);
            }
          }, 450);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearPendingDraw();
    };
  }, [clearPendingDraw, deckSearchState, drawSelectedDeckToHand, handleDeckOperation, recordHistoryEntry, resetZoom, setDeckSearchState, tabletopState.decks]);

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleDeckSearchMouseMove);
      window.removeEventListener('mouseup', handleDeckSearchMouseUp);
    };
  }, [handleDeckSearchMouseMove, handleDeckSearchMouseUp]);

  const handleContainerMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) {
      return;
    }

    if (dragState.draggingItem) {
      return;
    }

    const target = e.target as HTMLElement;
    const panSurface = target.closest('[data-pan-surface="true"]');
    if (target === canvasRef.current || panSurface || target === containerRef.current) {
      clearSelection();
      if (containerRef.current) {
        handleTableDragStart(e, containerRef.current.getBoundingClientRect());
      }
    }
  }, [canvasRef, containerRef, dragState.draggingItem, handleTableDragStart, clearSelection]);

  // Handle mouse events with container context
  const handleContainerMouseMove = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    
    // If we're dragging a card or deck, handle with drag handler
    if (dragState.draggingItem) {
      const rect = containerRef.current.getBoundingClientRect();
      handleDragMove(e, rect);
    } else if (isTableDragging && containerRef.current) {
      // If we're dragging the table, use table drag handler
      handleTableDrag(e, containerRef.current.getBoundingClientRect());
    }
  }, [dragState.draggingItem, handleDragMove, isTableDragging, handleTableDrag]);

  const handleContainerMouseUp = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    
    // If we have a dragging card or deck, handle with drag end
    if (dragState.draggingItem) {
      const rect = containerRef.current.getBoundingClientRect();
      handleDragEnd(e, rect);
    }
    
    // Always end the table drag
    handleTableDragEnd();
  }, [containerRef, dragState.draggingItem, handleDragEnd, handleTableDragEnd]);

  // Handle item drag start with container context
  const wrappedItemDragStart = useCallback((id: string, type: TabletopItemType, event: React.MouseEvent) => {
    if (containerRef.current) {
      handleItemDragStart(id, type, event, containerRef.current.getBoundingClientRect());
    }
  }, [containerRef, handleItemDragStart]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (!event.shiftKey) {
        return;
      }

      handleZoom(event, container.getBoundingClientRect());
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleZoom]);

  const selectedCardIdSet = useMemo(() => new Set(selectedCardIds), [selectedCardIds]);
  const orderedTableCards = useMemo(
    () => sortCardIdsByLayer(Object.keys(tabletopState.cards), tabletopState.cards)
      .map(cardId => tabletopState.cards[cardId])
      .filter((card): card is NonNullable<typeof card> => Boolean(card)),
    [tabletopState.cards]
  );

  return (
    <div 
      ref={containerRef} 
      className={`relative h-full w-full overflow-hidden ${isTableDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{ backgroundColor: bgColor || undefined }}
      onMouseDown={handleContainerMouseDown}
      onMouseMove={handleContainerMouseMove}
      onMouseUp={handleContainerMouseUp}
      onMouseLeave={handleContainerMouseUp}
    >
      <div className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at top, rgba(255, 255, 255, 0.22), transparent 34%), linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent 30%, rgba(15, 23, 42, 0.08))'
          }}
        />
      </div>
      <div 
        className="absolute inset-0 z-0 origin-center"
        style={{ transform: invertView ? 'rotate(180deg)' : 'none' }}
      >
        {/* Inline canvas keeps event handling localized and avoids extra wrapper indirection */}
        <canvas
          ref={canvasRef}
          className={`absolute top-0 left-0 w-full h-full transition-all duration-200`}
          style={{
            touchAction: 'none', // Prevents browser handling of touch events
          }}
        />

        {/* Fixed background for cards and decks */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div
            className="absolute w-full h-full pointer-events-auto"
            data-pan-surface="true"
            style={{
              transform: `translate3d(${zoomState.position.x}px, ${zoomState.position.y}px, 0) scale(${zoomState.scale})`,
              transformOrigin: 'top left',
              willChange: 'transform',
              transition: isTableDragging || dragState.draggingItem ? 'none' : 'transform 0.12s ease-out',
              zIndex: dragState.draggingItem ? 60 : 'auto'
            }}
          >
          {/* Render decks */}
          {Object.values(tabletopState.decks).map(deck => {
            const isDeckSelected = selectedItem?.id === deck.id && selectedItem?.type === 'deck';
            const isDraggingHover = dragState.hoveringDeckId === deck.id;
            const isSearchHover = deckHoverPreview?.deckId === deck.id;
            const highlightZone: DeckInsertionPosition | null = isDraggingHover
              ? dragState.hoveringDeckZone
              : isSearchHover
                ? deckHoverPreview?.zone ?? null
                : null;
            const isDeckHighlighted = isDraggingHover || isSearchHover;
            return (
              <Deck
                key={deck.id}
                deck={deck}
                onDragStart={wrappedItemDragStart}
                onSelect={handleTableItemSelect}
                isSelected={isDeckSelected}
                isHighlighted={isDeckHighlighted}
                highlightZone={highlightZone}
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                selectionColor={playerSelectionColor}
              />
            );
          })}
          
          {/* Render individual cards */}
          {orderedTableCards.map(card => (
            <Card
              key={card.id}
              card={card}
              onDragStart={wrappedItemDragStart}
              onFlip={handleCardFlip}
              onSelect={handleTableItemSelect}
              onMouseEnter={setHoveredTableCardId}
              onMouseLeave={cardId => setHoveredTableCardId(current => (current === cardId ? null : current))}
              onAnimationComplete={handleCardAnimationComplete}
              isSelected={selectedCardIdSet.has(card.id)}
              isDragging={dragState.draggingItem?.id === card.id && dragState.draggingItem?.type === 'card'}
              width={CARD_WIDTH}
              height={CARD_HEIGHT}
              selectionColor={playerSelectionColor}
            />
          ))}
          
          {/* Render dice */}
          {Object.values(tabletopState.dice).map(die => (
            <Dice
              key={die.id}
              dice={die}
              onDragStart={wrappedItemDragStart}
              onSelect={handleTableItemSelect}
              onRoll={handleDiceRoll}
              isSelected={selectedItem?.id === die.id && selectedItem?.type === 'dice'}
            />
          ))}

          {/* Render tokens */}
          {Object.values(tabletopState.tokens || {}).map(token => (
            <Token
              key={token.id}
              token={token}
              onDragStart={wrappedItemDragStart}
              onSelect={handleTableItemSelect}
              onDoubleClick={handleEditToken}
              isSelected={selectedItem?.id === token.id && selectedItem?.type === 'token'}
              viewerRotation={invertView ? 180 : 0}
            />
          ))}

          {/* Render coins */}
          {Object.values(tabletopState.coins || {}).map(coin => (
            <Coin
              key={coin.id}
              coin={coin}
              onDragStart={wrappedItemDragStart}
              onSelect={handleTableItemSelect}
              onFlip={handleCoinFlip}
              isSelected={selectedItem?.id === coin.id && selectedItem?.type === 'coin'}
              viewerRotation={invertView ? 180 : 0}
            />
          ))}
        </div>
      </div>
      </div>
      
      {/* Zoom indicator */}
      <div
        className="table-panel fixed bottom-4 left-4 z-10 rounded-full px-3 py-2 text-sm text-slate-700 transition-opacity duration-200 dark:text-slate-300 sm:left-5"
        style={{ opacity: zoomState.scale !== 1 ? 0.92 : 0 }}
      >
        Zoom: {Math.round(zoomState.scale * 100)}%
      </div>

      <TabletopTopBar
        status={sessionStatus}
        players={sessionPlayers}
        sessionId={activeSessionId || sessionIdInput.trim() || null}
        onOpenPlayers={() => setActivePanel('players')}
        onOpenChat={() => setActivePanel('chat')}
        onOpenMenu={() => setActivePanel('menu')}
        onResetView={resetZoom}
        onToggleInvertView={() => setInvertViewOverride(!invertView)}
        invertView={invertView}
        hasUnreadChat={chatMessages.length > lastSeenChatCount}
      />

      <CommandDock
        onCommand={handleCommand}
        activePanel={activePanel}
        isConnected={sessionStatus === 'connected'}
      />

      <ContextActionBar
        selectedItem={selectedItem}
        selectedHandCardId={selectedHandCardId}
        selectedCardIds={selectedCardIds}
        tabletopState={tabletopState}
        onDeckDrawOne={deckId => handleDeckOperation(deckId, { type: 'drawToHand', count: 1 })}
        onDeckSearch={handleDeckSearchOpen}
        onDeckShuffle={deckId => handleDeckOperation(deckId, { type: 'shuffle' })}
        onDeckShare={handleDeckShare}
        onCardsFlip={handleFlipCards}
        onCardsRotate={handleRotateCards}
        onCardsToHand={handleMoveCardsToHand}
        onCardInspect={cardId => setZoomedCard(cardId)}
        onCardsStack={handleStackSelectedCards}
        onDiceRoll={handleDiceRoll}
        onCoinFlip={handleCoinFlip}
        onTokenEdit={handleEditToken}
        onDelete={handleDeleteSelectedItem}
        onClear={clearSelection}
        topOffsetPx={actionBarTopOffset}
      />
      
      {/* Notification */}
      <TabletopNotification 
        notification={notification}
        onClear={() => setNotification(null)}
      />
      
      {/* Quick Deck Buttons for easy deck access */}
      <QuickDeckButtons 
        onImportDeck={handleDeckImport}
        initialPosition={centerPosition}
        isOpen={isQuickDecksOpen || activePanel === 'decks'}
        onClose={() => {
          setIsQuickDecksOpen(false);
          setActivePanel(prev => (prev === 'decks' ? null : prev));
        }}
        onShowDeckImporter={() => setShowDeckImporter(true)}
      />

      <TableDrawer
        title="Players"
        eyebrow="Room sync"
        isOpen={activePanel === 'players'}
        onClose={() => setActivePanel(null)}
      >
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <span className="table-chip">{sessionStatus}</span>
            {(activeSessionId || sessionIdInput.trim()) && (
              <span className="table-chip max-w-full truncate !normal-case !tracking-normal">
                {activeSessionId || sessionIdInput.trim()}
              </span>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Session ID
              <input
                type="text"
                value={sessionIdInput}
                onChange={event => setSessionIdInput(event.target.value)}
                placeholder="e.g. alpha-table"
                className="mt-1.5 w-full rounded-xl border border-slate-900/8 bg-white/75 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 dark:border-white/10 dark:bg-slate-950/60 dark:text-slate-100"
              />
            </label>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Display Name
              <input
                type="text"
                value={playerNameInput}
                onChange={event => setPlayerNameInput(event.target.value)}
                placeholder="Your name"
                className="mt-1.5 w-full rounded-xl border border-slate-900/8 bg-white/75 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 dark:border-white/10 dark:bg-slate-950/60 dark:text-slate-100"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={sessionStatus === 'idle' ? handleConnect : handleDisconnect}
                className={`rounded-xl px-3 py-2 text-sm font-semibold text-white transition ${
                  sessionStatus === 'idle' ? 'table-button-primary' : 'bg-rose-500 hover:bg-rose-600'
                }`}
              >
                {sessionStatus === 'idle' ? 'Connect' : 'Disconnect'}
              </button>
              <button type="button" onClick={() => setActivePanel('chat')} className="table-button-secondary rounded-xl px-3 py-2 text-sm font-semibold">
                Open chat
              </button>
            </div>
            {sessionError && (
              <p className="rounded-xl bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
                {sessionError}
              </p>
            )}
          </div>

          <div className="border-t border-slate-900/8 pt-4 dark:border-white/8">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Connected players
              </h3>
              <span className="text-xs text-slate-500 dark:text-slate-400">{sessionPlayers.length}</span>
            </div>
            {sessionPlayers.length === 0 ? (
              <p className="rounded-xl bg-white/35 px-3 py-3 text-sm text-slate-500 dark:bg-white/5 dark:text-slate-400">No players connected.</p>
            ) : (
              <ul className="space-y-2">
                {[...sessionPlayers].sort((a, b) => a.name.localeCompare(b.name)).map(player => {
                  const playerColor = getPlayerColor(player.id);
                  return (
                    <li key={player.id} className="flex items-center justify-between rounded-xl bg-white/45 px-3 py-2 text-sm dark:bg-white/5">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: playerColor.hex }} />
                        <span className="truncate text-slate-800 dark:text-slate-100">{player.id === currentPlayerId ? 'You' : player.name}</span>
                      </span>
                      <span className={`h-2 w-2 rounded-full ${player.isConnected ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </TableDrawer>

      <TableDrawer
        title="Chat"
        eyebrow="Live room messages"
        isOpen={activePanel === 'chat'}
        onClose={() => setActivePanel(null)}
      >
        <div className="flex h-full min-h-[24rem] flex-col">
          {sessionStatus !== 'connected' ? (
            <div className="rounded-xl bg-white/35 px-4 py-3 text-sm text-slate-600 dark:bg-white/5 dark:text-slate-300">
              Connect to a room before chatting.
            </div>
          ) : (
            <>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-xl bg-white/20 p-3 dark:bg-black/10">
                {chatMessages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">No messages yet.</p>
                ) : (
                  chatMessages.slice().reverse().map(message => {
                    const isOwnMessage = message.playerId === currentPlayerId;
                    const playerColor = getPlayerColor(message.playerId);
                    return (
                      <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[86%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                          isOwnMessage
                            ? 'bg-blue-600 text-white'
                            : 'bg-white/65 text-slate-800 dark:bg-white/8 dark:text-slate-200'
                        }`}>
                          <div className="mb-1 flex items-center gap-1.5 text-[10px] opacity-80">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: playerColor.hex }} />
                            <span>{isOwnMessage ? 'You' : message.playerName}</span>
                            <span>{formatChatTime(message.timestamp)}</span>
                          </div>
                          <p className="break-words leading-relaxed">{message.text}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={event => setChatInput(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleSendChatFromDrawer();
                    }
                  }}
                  placeholder="Type a message..."
                  maxLength={500}
                  className="min-w-0 flex-1 rounded-xl border border-slate-900/8 bg-white/75 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 dark:border-white/10 dark:bg-slate-950/60 dark:text-slate-100"
                />
                <button type="button" onClick={handleSendChatFromDrawer} className="table-button-primary rounded-xl px-4 py-2 text-sm font-semibold">
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      </TableDrawer>

      <TableDrawer
        title="History"
        eyebrow="Recent table activity"
        isOpen={activePanel === 'history'}
        onClose={() => setActivePanel(null)}
      >
        {(tabletopState.history || []).length === 0 ? (
          <p className="rounded-xl bg-white/35 px-4 py-3 text-sm text-slate-500 dark:bg-white/5 dark:text-slate-400">
            Actions you perform will appear here.
          </p>
        ) : (
          <ul className="space-y-2">
            {(tabletopState.history || []).slice(0, 24).map(entry => (
              <li key={entry.id} className="rounded-xl bg-white/45 px-3 py-2 dark:bg-white/5">
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">{entry.message}</p>
                <span className="mt-1 block text-[10px] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </TableDrawer>

      <TableDrawer
        title="Controls"
        eyebrow="Desktop accelerators"
        isOpen={activePanel === 'help'}
        onClose={() => setActivePanel(null)}
      >
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Table</h3>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Drag empty table space to pan.</li>
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Shift + scroll zooms in and out.</li>
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Home resets the view.</li>
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Selected items</h3>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Use the contextual toolbar for draw, search, flip, rotate, inspect, and delete.</li>
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Hover or select a card and press Z to inspect it. Press Z again to close the zoom.</li>
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Right-click a card still flips it on desktop.</li>
              <li className="rounded-xl bg-white/40 px-3 py-2 dark:bg-white/5">Drag cards onto your hand rail or onto a deck to move them.</li>
            </ul>
          </div>
        </div>
      </TableDrawer>

      <TableDrawer
        title="Menu"
        eyebrow="Table actions"
        isOpen={activePanel === 'menu'}
        onClose={() => setActivePanel(null)}
      >
        <div className="grid gap-2">
          {[
            { label: 'Deck Library', action: () => setActivePanel('decks') },
            { label: 'Players', action: () => setActivePanel('players') },
            { label: 'Chat', action: () => setActivePanel('chat') },
            { label: 'History', action: () => setActivePanel('history') },
            { label: 'Controls', action: () => setActivePanel('help') }
          ].map(item => (
            <button
              key={item.label}
              type="button"
              onClick={item.action}
              className="table-button-secondary rounded-xl px-4 py-3 text-left text-sm font-semibold"
            >
              {item.label}
            </button>
          ))}
        </div>
      </TableDrawer>
      
      <OpponentHands
        hands={opponentHands}
        viewerId={viewerId}
        playerNames={playerNameById}
        selectedCardId={selectedHandCardId}
        onSelectCard={cardId => handleSelectHandCard(cardId)}
        onBoundsChange={setOpponentHandsBounds}
        selectionColor={playerSelectionColor ?? null}
      />
      
      {/* Private hand rail */}
      <HandRail
        cards={myHandCards}
        onCardMouseDown={handleHandCardMouseDown}
        onCardMouseEnter={setHoveredHandCardId}
        onCardMouseLeave={cardId => setHoveredHandCardId(current => (current === cardId ? null : current))}
        onBoundsChange={handleHandBoundsChange}
        isHighlighted={isHandHighlighted}
        isDraggingFromHand={Boolean(dragState.draggingItem?.type === 'card' && dragState.origin === 'hand')}
        viewerId={viewerId}
        playerNames={playerNameById}
        onFlipCard={handleHandCardFlip}
        onSelectCard={handleSelectHandCard}
        selectedCardId={selectedHandCardId}
        selectionColor={playerSelectionColor ?? null}
      />
      
      {/* Zoomed Card Modal */}
      {zoomedCard && tabletopState.cards[zoomedCard] && (
        <ZoomedCardModal
          card={tabletopState.cards[zoomedCard]}
          onClose={() => setZoomedCard(null)}
          onFlip={handleCardFlip}
          width={ZOOMED_CARD_WIDTH}
          height={ZOOMED_CARD_HEIGHT}
        />
      )}

      {/* Token Edit Modal */}
      <TokenEditModal
        token={editingTokenId && tabletopState.tokens ? tabletopState.tokens[editingTokenId] : null}
        onSave={handleSaveToken}
        onClose={() => setEditingTokenId(null)}
      />

      {/* Hand card preview */}
      <HandCardPreviewModal
        card={previewHandCard}
        onClose={handleCloseHandPreview}
        viewerId={viewerId}
      />

      {deckSearchState && activeSearchDeck && (
        <DeckSearchModal
          deck={activeSearchDeck}
          selectedCardIds={deckSearchState.selectedCardIds}
          onSelectionChange={handleDeckSearchSelectionChange}
          onClose={handleCloseDeckSearch}
          onRequestDrag={handleDeckSearchDragStart}
          zoomedCardId={zoomedDeckSearchCard?.id ?? null}
          onZoomCard={setZoomedDeckSearchCard}
        />
      )}

      {zoomedDeckSearchCard && (
        <ZoomedCardModal
          card={zoomedDeckSearchCard}
          onClose={() => setZoomedDeckSearchCard(null)}
        />
      )}
      
      {/* Deck Importer Modal */}
      {showDeckImporter && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div 
            className="absolute inset-0 bg-slate-950/18 backdrop-blur-sm"
            onClick={() => setShowDeckImporter(false)}
          ></div>
          <div className="table-panel-strong relative z-10 mx-4 w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Custom Deck
                </p>
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Import Deck</h2>
              </div>
              <button 
                onClick={() => setShowDeckImporter(false)}
                className="table-button-secondary rounded-full px-3 py-1.5 text-xs font-semibold"
              >
                Close
              </button>
            </div>
            
            <div className="mb-4">
              <DeckImporter 
                onImport={(deck) => {
                  handleDeckImport({
                    ...deck,
                    position: centerPosition
                  });
                  setShowDeckImporter(false);
                }}
                position={centerPosition}
              />
            </div>
          </div>
        </div>
      )}

      {searchDragPreview && (
        <div
          className="pointer-events-none fixed z-[70]"
          style={{
            left: searchDragPreview.clientX + 12,
            top: searchDragPreview.clientY + 12
          }}
        >
          {searchDragPreview.cards.map((card, index) => (
            <div
              key={card.id}
              className="relative overflow-hidden rounded-md border border-slate-700/70 bg-slate-900/80 shadow-xl"
              style={{
                transform: `translate(${index * 10}px, ${index * 14}px)`
              }}
            >
              {card.imageUrl ? (
                <img
                  src={card.imageUrl}
                  alt={card.name ?? 'Card'}
                  className="h-28 w-20 object-cover"
                  draggable={false}
                />
              ) : (
                <div className="flex h-28 w-20 items-center justify-center px-2 text-xs text-slate-200">
                  {card.name ?? 'Card'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 
